import { useMemo, useCallback, useState, useEffect } from "react";
import {
  Edit2,
  Trash,
  X,
  Download,
  Plus,
  Pencil,
  User,
  Clock,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, Popconfirm, Pagination, ConfigProvider, Tooltip } from "antd";
import { get } from "@/services/axiosUitils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  DynamicModuleService,
  SchemaService,
  DropdownService,
} from "@/services/APIs";
import { notify } from "@/components/common/Notification";
import CustomButton from "@/components/common/Button";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { createForm } from "@formily/core";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { checkFormChanges } from "@/lib/checkFormChanges";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { getEmployeeId } from "@/lib/storeManager";
import {
  isEmployee,
  canAccessAttendanceAndCheckin,
} from "../../utils/rolePermissions";

const EMPLOYEE_CHECKIN_SCHEMA_NAME = "PTS Employee Checkin";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";

/** Format date as "YYYY-MM-DD HH:mm:ss" for check-in API */
const formatDateTimeLocal = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;

// Component to fetch and display employee name in tooltip
const EmployeeTooltipContent = ({
  userId,
}: {
  userId: string | null | undefined;
}) => {
  const { data: employeeData, isLoading } = useQuery({
    queryKey: ["employee-by-user-id", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      try {
        // Use encodeURIComponent to properly encode the model name
        const modelName = encodeURIComponent(EMPLOYEE_SCHEMA_NAME);
        const response = await get<any>(
          `/formily/dynamic-model/${modelName}/`,
          {
            params: {
              users__eq: userId,
            },
          }
        );
        return response;
      } catch (error) {
        console.error("Error fetching employee data:", error);
        return null;
      }
    },
    enabled: !!userId,
  });

  // Extract employee name from response
  const employeeName = useMemo(() => {
    if (!employeeData) {
      return "Loading...";
    }

    // Handle different response formats
    const results =
      employeeData?.results ??
      employeeData?.data ??
      (Array.isArray(employeeData) ? employeeData : []);

    if (results.length === 0) {
      return "Unknown Employee";
    }

    const employee = results[0];

    // Try to find first_name and last_name fields
    const firstName =
      employee.first_name ??
      employee.firstname ??
      employee.fname ??
      employee.firstName ??
      "";
    const lastName =
      employee.last_name ??
      employee.lastname ??
      employee.lname ??
      employee.lastName ??
      "";

    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unknown Employee";
  }, [employeeData]);

  if (isLoading) {
    return <span className="text-gray-700">Loading...</span>;
  }

  return <span className="text-gray-700">{employeeName}</span>;
};

interface CheckinRecord {
  id: string;
  employee: string;
  employeeName: string;
  firstName: string;
  lastName: string;
  date: string;
  time: string;
  logType: string;
}

const EmployeeCheckinPage = () => {
  const queryClient = useQueryClient();
  const employeeId = getEmployeeId();

  // Check user role permissions (HR and PTS_Admin have same rights for Employee Check-in)
  const userIsEmployee = isEmployee();
  const userCanManageCheckin = canAccessAttendanceAndCheckin();

  // Calculate default dates (both set to today)
  const getDefaultDates = useCallback(() => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0]; // YYYY-MM-DD format
    return {
      startDate: todayStr,
      endDate: todayStr,
    };
  }, []);

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.startDate);
  const [endDate, setEndDate] = useState<string>(defaultDates.endDate);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(200);

  // Selected rows state for export
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useMemo(() => createForm({}), []);

  // Set selected employee based on role
  // If employee role: always use their own employeeId (cannot change)
  // If admin/user role: default to "all" (can select any employee)
  const [selectedEmployee, setSelectedEmployee] = useState<string>(() => {
    if (userIsEmployee && employeeId) {
      return employeeId;
    }
    return "all"; // Default to "all" for admin/user roles
  });

  // LogType filter state
  const [selectedLogType, setSelectedLogType] = useState<string>("all");

  // Update selectedEmployee when employeeId is available (only for employee role)
  useEffect(() => {
    if (userIsEmployee && employeeId) {
      // Lock to employee's own ID if they have employee role
      setSelectedEmployee(employeeId);
    }
  }, [employeeId, userIsEmployee]);

  // Format date for API (YYYY-MM-DDTHH:mm:ss)
  // Start date: 00:00:00, End date: 23:59:59
  const formatDateForAPI = useCallback(
    (dateString: string, isEndDate: boolean = false) => {
      if (!dateString) {
        return null;
      }
      const [year, month, day] = dateString.split("-").map(Number);
      if (isEndDate) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59`;
      } else {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
      }
    },
    []
  );

  // Fetch column grid data to find field names
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_CHECKIN_SCHEMA_NAME],
    queryFn: () =>
      SchemaService.getColumnGridData(EMPLOYEE_CHECKIN_SCHEMA_NAME),
  });

  // Fetch schema for modal form
  const { data: schemaInfo } = useQuery({
    queryKey: ["schema", EMPLOYEE_CHECKIN_SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(EMPLOYEE_CHECKIN_SCHEMA_NAME),
    enabled: !!EMPLOYEE_CHECKIN_SCHEMA_NAME,
  });

  // Fetch record data for edit mode
  const { data: recordInfo } = useQuery({
    queryKey: [
      "dynamic-model",
      EMPLOYEE_CHECKIN_SCHEMA_NAME,
      editingRecord?.id,
    ],
    queryFn: () => {
      if (!editingRecord?.id) {
        throw new Error("Record ID is required");
      }
      return DynamicModuleService.getItemById(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        editingRecord.id
      );
    },
    enabled: modalOpen && !!editingRecord?.id,
  });

  // Fetch employee dropdown for employee name mapping
  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Fetch employee data to get first_name and last_name
  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => {
      return DynamicModuleService.getGridColItems(
        EMPLOYEE_SCHEMA_NAME,
        1,
        1000,
        {}
      );
    },
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Extract employee records
  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  // Get employee column grid data to find first_name and last_name fields
  const { data: employeeColumnGridData } = useQuery({
    queryKey: ["columnGridData", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(EMPLOYEE_SCHEMA_NAME),
  });

  // Find employee field names
  const findEmployeeFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!employeeColumnGridData?.grid) {
        return null;
      }
      for (const field of employeeColumnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [employeeColumnGridData]
  );

  const employeeFirstNameField = findEmployeeFieldName([
    "first_name",
    "firstname",
    "fname",
  ]);
  const employeeLastNameField = findEmployeeFieldName([
    "last_name",
    "lastname",
    "lname",
  ]);

  // Transform employee options to show first name + last name
  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions || !employeeRecords.length) {
      return employeeOptions ?? [];
    }

    return employeeOptions.map((employee: { value: string; label: string }) => {
      // Find the employee record by ID
      const employeeRecord = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(employee.value)
      );

      if (employeeRecord) {
        // Get first name and last name from the record
        const firstName =
          (employeeFirstNameField && employeeRecord[employeeFirstNameField]) ??
          employeeRecord.first_name ??
          employeeRecord.firstname ??
          employeeRecord.fname ??
          "";
        const lastName =
          (employeeLastNameField && employeeRecord[employeeLastNameField]) ??
          employeeRecord.last_name ??
          employeeRecord.lastname ??
          employeeRecord.lname ??
          "";

        // Combine first name and last name
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          value: employee.value,
          label: fullName || employee.label, // Fallback to original label if names not found
        };
      }

      // Fallback to original label if employee record not found
      return employee;
    });
  }, [
    employeeOptions,
    employeeRecords,
    employeeFirstNameField,
    employeeLastNameField,
  ]);

  // Find field names from schema (moved up to use in query)
  const findFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(term => {
            const lowerTerm = term.toLowerCase();
            return (
              fieldName.includes(lowerTerm) ?? fieldTitle.includes(lowerTerm)
            );
          })
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [columnGridData]
  );

  // Get field names (moved up to use in query)
  const employeeField = findFieldName([
    "employee",
    "employee_id",
    "emp_id",
    "empname",
  ]);
  const dateField = findFieldName([
    "date",
    "checkin_date",
    "check_in_date",
    "attendance_date",
    "work_date",
  ]);
  const timeField = findFieldName([
    "time",
    "checkin_time",
    "check_in_time",
    "timestamp",
    "datetime",
  ]);
  const logTypeField = findFieldName([
    "logtype",
    "log_type",
    "type",
    "check_type",
  ]);

  // Fetch checkin data with server-side pagination
  const { data: checkinDataResponse, isLoading } = useQuery({
    queryKey: [
      "employee-checkin-data",
      EMPLOYEE_CHECKIN_SCHEMA_NAME,
      startDate,
      endDate,
      selectedEmployee,
      selectedLogType,
      currentPage,
      pageSize,
      logTypeField, // Include logTypeField in queryKey to refetch when field is found
    ],
    queryFn: () => {
      const paramsFilter: any = {};

      // Add date filters
      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.datetime__gte = formattedStartDate;
        }
      }
      if (endDate && endDate.trim() !== "") {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.datetime__lte = formattedEndDate;
        }
      }

      // Add employee ID filter if a specific employee is selected (not "all")
      if (selectedEmployee && selectedEmployee !== "all") {
        paramsFilter.employee__eq = selectedEmployee;
      }

      // Add logType filter server-side if a specific logType is selected (not "all")
      if (selectedLogType && selectedLogType !== "all" && logTypeField) {
        paramsFilter[`${logTypeField}__eq`] = selectedLogType;
      }

      paramsFilter.ordering = '["-datetime", "employee.firstname"]';

      // Use getItemsByPage to preserve full response with count for pagination
      return DynamicModuleService.getItemsByPage(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        currentPage,
        pageSize,
        paramsFilter
      );
    },
    enabled: !!EMPLOYEE_CHECKIN_SCHEMA_NAME,
  });

  // Extract data from response
  const checkinRecords = useMemo(() => {
    if (!checkinDataResponse) {
      return [];
    }
    if (Array.isArray(checkinDataResponse)) {
      return checkinDataResponse;
    }
    if (
      checkinDataResponse.results &&
      Array.isArray(checkinDataResponse.results)
    ) {
      return checkinDataResponse.results;
    }
    return [];
  }, [checkinDataResponse]);

  // Extract total count from response
  const totalCount = useMemo(() => {
    if (!checkinDataResponse) {
      return 0;
    }
    if (Array.isArray(checkinDataResponse)) {
      return checkinDataResponse.length;
    }
    if (
      typeof checkinDataResponse === "object" &&
      checkinDataResponse.count !== undefined
    ) {
      return checkinDataResponse.count;
    }
    if (
      checkinDataResponse.results &&
      Array.isArray(checkinDataResponse.results)
    ) {
      return checkinDataResponse.count ?? checkinDataResponse.results.length;
    }
    return 0;
  }, [checkinDataResponse]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRows(new Set()); // Clear selected rows when filters change
  }, [startDate, endDate, selectedEmployee, selectedLogType]);

  // Get employee name by ID
  const getEmployeeNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id || !employeeOptions) {
        return "-";
      }
      const employee = employeeOptions.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      return employee?.label ?? "-";
    },
    [employeeOptions]
  );

  // Get employee first and last name by ID
  const getEmployeeNamesById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return { firstName: "-", lastName: "" };
      }

      const employee = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(id)
      );

      if (!employee) {
        // Fallback to dropdown label if employee record not found
        const dropdownEmployee = employeeOptions?.find(
          (item: { value: string; label: string }) =>
            String(item.value) === String(id)
        );
        if (dropdownEmployee?.label) {
          const nameParts = dropdownEmployee.label.split(" ");
          return {
            firstName: nameParts[0] ?? "-",
            lastName: nameParts.slice(1).join(" ") ?? "",
          };
        }
        return { firstName: "-", lastName: "" };
      }

      const firstName =
        (employeeFirstNameField && employee[employeeFirstNameField]) ??
        employee.first_name ??
        employee.firstname ??
        employee.fname ??
        "";
      const lastName =
        (employeeLastNameField && employee[employeeLastNameField]) ??
        employee.last_name ??
        employee.lastname ??
        employee.lname ??
        "";

      return {
        firstName: firstName ?? "-",
        lastName: lastName ?? "",
      };
    },
    [
      employeeRecords,
      employeeOptions,
      employeeFirstNameField,
      employeeLastNameField,
    ]
  );

  // Format date helper - DD-MM-YYYY
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
      return "-";
    }
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  // Format time helper
  const formatTime = (timeString: string | null | undefined): string => {
    if (!timeString) {
      return "-";
    }
    try {
      // Handle ISO date strings
      const date = new Date(timeString);
      if (!isNaN(date.getTime())) {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const period = hours >= 12 ? "PM" : "AM";
        const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
        return `${String(displayHour).padStart(2, "0")}:${String(
          minutes
        ).padStart(2, "0")} ${period}`;
      }
      // Handle time strings like "HH:MM:SS"
      const [hours, minutes] = timeString.split(":");
      if (hours && minutes) {
        const hour = parseInt(hours, 10);
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${String(displayHour).padStart(2, "0")}:${minutes} ${period}`;
      }
      return timeString;
    } catch {
      return timeString;
    }
  };

  // Helper function to format date and time for edited tooltip
  const formatDateTime = useCallback(
    (dateString: string | null | undefined) => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        // Format: "Dec 28, 10:30 AM" or similar
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        const displayMinutes = minutes.toString().padStart(2, "0");
        return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
      } catch {
        return dateString;
      }
    },
    []
  );

  // Fetch all unique log types for dropdown options (separate query without pagination)
  const { data: allLogTypesData } = useQuery({
    queryKey: [
      "all-log-types",
      EMPLOYEE_CHECKIN_SCHEMA_NAME,
      startDate,
      endDate,
      selectedEmployee,
    ],
    queryFn: () => {
      const paramsFilter: any = {};

      // Add date filters
      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.datetime__gte = formattedStartDate;
        }
      }
      if (endDate && endDate.trim() !== "") {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.datetime__lte = formattedEndDate;
        }
      }

      // Add employee ID filter if a specific employee is selected (not "all")
      if (selectedEmployee && selectedEmployee !== "all") {
        paramsFilter.employee__eq = selectedEmployee;
      }

      paramsFilter.ordering = '["-datetime", "employee.firstname"]';

      // Use getItemsByPage to preserve full response with count
      return DynamicModuleService.getItemsByPage(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        1,
        1000, // Large page size to get all records for log type extraction
        paramsFilter
      );
    },
    enabled: !!EMPLOYEE_CHECKIN_SCHEMA_NAME && !!logTypeField,
  });

  // Extract all log type records
  const allLogTypeRecords = useMemo(() => {
    if (!allLogTypesData) {
      return [];
    }
    if (Array.isArray(allLogTypesData)) {
      return allLogTypesData;
    }
    if (allLogTypesData.results && Array.isArray(allLogTypesData.results)) {
      return allLogTypesData.results;
    }
    return [];
  }, [allLogTypesData]);

  // Get unique log types from all records for dropdown options
  const logTypeOptions = useMemo(() => {
    const logTypes = new Set<string>();
    allLogTypeRecords.forEach((record: Record<string, any>) => {
      const logType =
        (logTypeField && record[logTypeField]) ??
        record.logType ??
        record.log_type ??
        record.type ??
        "";
      if (logType && String(logType).trim() !== "") {
        logTypes.add(String(logType));
      }
    });
    return Array.from(logTypes).sort();
  }, [allLogTypeRecords, logTypeField]);

  // Helper function to map a single record to CheckinRecord format
  const mapRecordToCheckinRecord = useCallback(
    (record: any): CheckinRecord => {
      const recordId =
        record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";

      const employeeId =
        (employeeField && record[employeeField]) ??
        record.employee ??
        record.employee_id ??
        null;

      const employeeNames = getEmployeeNamesById(employeeId);

      // Get date and time - if timeField contains datetime, extract both
      const dateTimeValue =
        (timeField && record[timeField]) ??
        record.time ??
        record.checkin_time ??
        record.timestamp ??
        record.datetime ??
        "";

      // Try to extract date from datetime field if it's a full datetime string
      let dateValue = "";
      let timeValue = "";

      if (dateTimeValue) {
        try {
          const date = new Date(dateTimeValue);
          if (!isNaN(date.getTime())) {
            // It's a valid datetime, extract both date and time
            dateValue = dateTimeValue;
            timeValue = dateTimeValue;
          } else {
            // Not a datetime, might be separate fields
            timeValue = dateTimeValue;
          }
        } catch {
          timeValue = dateTimeValue;
        }
      }

      // If dateField exists, use it; otherwise use the datetime value
      const finalDateValue =
        (dateField && record[dateField]) ??
        record.date ??
        record.checkin_date ??
        record.attendance_date ??
        dateValue ??
        "";

      return {
        id: String(recordId),
        employee: String(employeeId ?? ""),
        employeeName: getEmployeeNameById(employeeId),
        firstName: employeeNames.firstName,
        lastName: employeeNames.lastName,
        date: finalDateValue,
        time: timeValue,
        logType:
          (logTypeField && record[logTypeField]) ??
          record.logType ??
          record.log_type ??
          record.type ??
          "-",
      };
    },
    [
      employeeField,
      dateField,
      timeField,
      logTypeField,
      getEmployeeNameById,
      getEmployeeNamesById,
    ]
  );

  // Map raw data to CheckinRecord interface (logType filtering is now server-side)
  const mappedRecords = useMemo(() => {
    return checkinRecords.map((record: any) => ({
      ...mapRecordToCheckinRecord(record),
      rawRecord: record, // Keep reference to raw record for edited check
    }));
  }, [checkinRecords, mapRecordToCheckinRecord]);

  // Mutation for deleting check-in record
  const deleteCheckinMutation = useMutation({
    mutationFn: async (id: string) => {
      return await DynamicModuleService.deleteItem(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        id
      );
    },
    onSuccess: () => {
      // notify.success("Check-in record deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["employee-checkin-data", EMPLOYEE_CHECKIN_SCHEMA_NAME],
      });
      queryClient.invalidateQueries({
        queryKey: ["all-log-types", EMPLOYEE_CHECKIN_SCHEMA_NAME],
      });
    },
    onError: (error: any) => {
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to delete check-in record"
      );
    },
  });

  // Mutation for bulk deleting check-in records
  const bulkDeleteCheckinMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // Delete all records in parallel
      const deletePromises = ids.map(id =>
        DynamicModuleService.deleteItem(EMPLOYEE_CHECKIN_SCHEMA_NAME, id)
      );
      return await Promise.all(deletePromises);
    },
    onSuccess: () => {
      // notify.success(
      //   `Successfully deleted ${selectedRows.size} check-in record(s)`
      // );
      setSelectedRows(new Set()); // Clear selection after deletion
      queryClient.invalidateQueries({
        queryKey: ["employee-checkin-data", EMPLOYEE_CHECKIN_SCHEMA_NAME],
      });
      queryClient.invalidateQueries({
        queryKey: ["all-log-types", EMPLOYEE_CHECKIN_SCHEMA_NAME],
      });
    },
    onError: (error: any) => {
      notify.error(
        error?.response?.data?.message ??
          error?.message ??
          "Failed to delete check-in records"
      );
    },
  });

  // Handler for deleting check-in record
  const handleDelete = useCallback(
    (id: string | undefined) => {
      if (!id) {
        notify.error("Record ID is missing");
        return;
      }
      deleteCheckinMutation.mutate(String(id));
    },
    [deleteCheckinMutation]
  );

  // Handler for bulk deleting check-in records
  const handleBulkDeleteCheckin = useCallback(() => {
    if (selectedRows.size === 0) {
      notify.error("Please select at least one record to delete");
      return;
    }

    const idsArray = Array.from(selectedRows);
    bulkDeleteCheckinMutation.mutate(idsArray);
  }, [selectedRows, bulkDeleteCheckinMutation]);

  // Modify schema to disable only Employee field when editing (Time remains editable)
  const getModifiedSchema = useCallback(() => {
    if (!schemaInfo) {
      return null;
    }

    const schema = schemaInfo.json ?? schemaInfo;
    const schemaCopy = JSON.parse(JSON.stringify(schema)); // Deep clone

    // Only disable fields when editing (not when adding)
    const isEditMode = !!editingRecord?.id;

    if (!isEditMode) {
      // In add mode, return schema as-is (all fields editable)
      return schemaCopy;
    }

    // Get field names (only Employee is disabled in edit mode; Time stays editable)
    const employeeFieldName = employeeField ?? "employee";

    // Helper function to recursively process schema properties
    const processProperties = (props: any) => {
      if (!props || typeof props !== "object") {
        return;
      }

      Object.keys(props).forEach(key => {
        const field = props[key];
        if (field && typeof field === "object") {
          // Check if this is the Employee field
          const keyLower = key.toLowerCase();
          const employeeFieldNameLower = employeeFieldName.toLowerCase();

          // Get field title for additional matching
          const fieldTitle = (
            field.title ??
            field["x-decorator-props"]?.title ??
            field["x-component-props"]?.title ??
            ""
          ).toLowerCase();
          const componentFieldName = field["x-component-props"]?.field_name
            ? String(field["x-component-props"].field_name).toLowerCase()
            : "";

          const isEmployeeField =
            keyLower === employeeFieldNameLower ||
            keyLower === "employee" ||
            keyLower === "employee_id" ||
            keyLower === "emp_id" ||
            keyLower === "empname" ||
            keyLower === "emp" ||
            componentFieldName === employeeFieldNameLower ||
            ["employee", "employee_id", "emp_id", "empname", "emp"].includes(
              componentFieldName
            ) ||
            ["employee", "employee_id", "emp_id", "empname", "emp"].includes(
              keyLower
            ) ||
            (fieldTitle &&
              (fieldTitle.includes("employee") ?? fieldTitle.includes("emp")));

          // Disable only Employee field when editing (Time field stays editable)
          if (isEmployeeField) {
            // Ensure x-component-props exists
            field["x-component-props"] ??= {};

            // Set disabled property - this is the key property for Formily
            field["x-component-props"].disabled = true;

            // Also set readOnly for extra safety
            field["x-decorator-props"] ??= {};
            field["x-decorator-props"].readOnly = true;

            // Set Formily pattern to readOnly (this is the most reliable way)
            field["x-pattern"] = "readOnly";

            // Set readOnly at component level as well
            field.readOnly = true;
            field["x-readonly"] = true;
            field["x-disabled"] = true;
          }

          // Recursively process nested properties if they exist
          if (field.properties) {
            processProperties(field.properties);
          }
          if (field.items?.properties) {
            processProperties(field.items.properties);
          }
        }
      });
    };

    // Get the actual schema properties
    let properties: any = null;
    if (schemaCopy.schema?.properties) {
      properties = schemaCopy.schema.properties;
    } else if (schemaCopy.properties) {
      properties = schemaCopy.properties;
    }

    if (properties) {
      processProperties(properties);
    }

    return schemaCopy;
  }, [schemaInfo, editingRecord, employeeField]);

  // Handle add - open modal for new record
  const handleAdd = () => {
    // Clear editing record first
    setEditingRecord(null);
    // Clear all form fields and initial values completely
    form.setInitialValues({});
    form.reset("*");
    form.setValues({});
    // Open modal after clearing
    setModalOpen(true);
  };

  // Handle edit - open modal with record data
  const handleEdit = (id: string | undefined) => {
    if (!id) {
      notify.error("Record ID is missing");
      return;
    }
    setEditingRecord({ id });
    setModalOpen(true);
  };

  // Set form initial values when record data is loaded
  useEffect(() => {
    if (!modalOpen) {
      // If modal is closed, clear form to prevent stale data
      form.setInitialValues({});
      form.reset("*");
      form.setValues({});
      return;
    }

    if (editingRecord?.id && recordInfo) {
      // Edit mode: set the record data
      const cleanedData = removeMetaFields(recordInfo);
      form.setInitialValues(cleanedData);
      form.setValues(cleanedData);
    } else if (!editingRecord) {
      // Add mode: clear all fields and initial values completely
      // Clear initial values first
      form.setInitialValues({});
      // Reset all fields
      form.reset("*");
      // Clear all current values
      form.setValues({});
      // Clear all field states to ensure no values remain
      const currentValues = form.values;
      if (currentValues && Object.keys(currentValues).length > 0) {
        Object.keys(currentValues).forEach(fieldPath => {
          form.setFieldState(fieldPath, state => {
            if (state) {
              state.value = undefined;
              state.initialValue = undefined;
            }
          });
        });
      }
    }
  }, [modalOpen, editingRecord, recordInfo, form]);

  // Handle modal save
  const handleModalSave = async () => {
    try {
      await form.validate();
      const modifiedValues = removeMetaFields(form.values);
      setIsSubmitting(true);

      if (editingRecord?.id) {
        // Edit mode - check for changes before updating
        if (!checkFormChanges(modifiedValues, recordInfo, notify)) {
          setIsSubmitting(false);
          return;
        }
        const response = await DynamicModuleService.updateItem(
          EMPLOYEE_CHECKIN_SCHEMA_NAME,
          editingRecord.id,
          modifiedValues
        );
        if (response) {
          // notify.success("Check-in record updated successfully");
          queryClient.invalidateQueries({
            queryKey: ["employee-checkin-data", EMPLOYEE_CHECKIN_SCHEMA_NAME],
          });
          queryClient.invalidateQueries({
            queryKey: ["all-log-types", EMPLOYEE_CHECKIN_SCHEMA_NAME],
          });
          notify.success("Check-in record updated successfully");
          setModalOpen(false);
          setEditingRecord(null);
          // Reset all form fields after update
          form.reset("*");
          form.setValues({});
        }
      } else {
        // Add mode – ensure datetime is "YYYY-MM-DD HH:mm:ss"
        const payload = { ...modifiedValues };
        const datetimeKey = timeField ?? "datetime";
        const rawDatetime = payload[datetimeKey];
        if (rawDatetime !== null) {
          payload[datetimeKey] = formatDateTimeLocal(new Date(rawDatetime));
        }
        const response = await DynamicModuleService.createItem(
          EMPLOYEE_CHECKIN_SCHEMA_NAME,
          // modifiedValues
          payload
        );
        if (response) {
          // notify.success("Check-in record created successfully");
          queryClient.invalidateQueries({
            queryKey: ["employee-checkin-data", EMPLOYEE_CHECKIN_SCHEMA_NAME],
          });
          queryClient.invalidateQueries({
            queryKey: ["all-log-types", EMPLOYEE_CHECKIN_SCHEMA_NAME],
          });
          notify.success("Check-in record created successfully");
          setModalOpen(false);
          setEditingRecord(null);
          // Reset all form fields after add
          form.reset("*");
          form.setValues({});
        }
      }
    } catch (error: any) {
      notify.error(
        error?.message ??
          `Failed to ${editingRecord?.id ? "update" : "create"} check-in record`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setModalOpen(false);
    setEditingRecord(null);
    // Reset all form fields when canceling
    form.reset("*");
    form.setValues({});
  };

  // Get LogType badge color
  const getLogTypeBadgeColor = (logType: string): string => {
    const logTypeLower = logType?.toLowerCase() ?? "";
    if (logTypeLower === "in" || logTypeLower.includes("in")) {
      return "bg-green-100 text-green-700";
    }
    if (logTypeLower === "out" || logTypeLower.includes("out")) {
      return "bg-red-100 text-red-700";
    }
    return "bg-blue-100 text-blue-700";
  };

  // Handle row selection
  const handleRowSelect = (recordId: string, checked: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(recordId);
      } else {
        newSet.delete(recordId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set<string>(
        mappedRecords
          .map((record: CheckinRecord) => record.id)
          .filter((id: string) => id !== "")
      );
      setSelectedRows(allIds);
    } else {
      setSelectedRows(new Set());
    }
  };

  // Check if all rows are selected
  const allRowsSelected = useMemo(() => {
    if (mappedRecords.length === 0) {
      return false;
    }
    return mappedRecords.every((record: CheckinRecord) =>
      selectedRows.has(record.id)
    );
  }, [mappedRecords, selectedRows]);

  // Get count of actually exportable selected records (only from current page)
  const exportableSelectedCount = useMemo(() => {
    return mappedRecords.filter((record: CheckinRecord) =>
      selectedRows.has(record.id)
    ).length;
  }, [mappedRecords, selectedRows]);

  // Helper function to format a record for CSV export
  const formatRecordForCSV = useCallback((record: CheckinRecord): string => {
    const employeeName = `${record.firstName} ${record.lastName}`.trim() || "-";
    const date = formatDate(record.date || record.time);
    const time = formatTime(record.time);
    const logType = record.logType || "-";

    return [`"${employeeName}"`, `"${date}"`, `"${time}"`, `"${logType}"`].join(
      ","
    );
  }, []);

  // Export selected rows to CSV
  const handleExportSelected = () => {
    if (selectedRows.size === 0) {
      return;
    }

    // Filter selected records - only from current page
    const selectedRecords = mappedRecords.filter((record: CheckinRecord) =>
      selectedRows.has(record.id)
    );

    // Verify count matches
    if (selectedRecords.length !== selectedRows.size) {
      notify.warning(
        `Warning: ${selectedRows.size} records selected, but only ${selectedRecords.length} records found on current page. Exporting available records.`
      );
    }

    if (selectedRecords.length === 0) {
      notify.error("No selected records found to export");
      setSelectedRows(new Set());
      return;
    }

    // Prepare CSV data
    const headers = ["Employee Name", "Date", "Time", "Log Type"];

    const csvRows = [
      headers.join(","),
      ...selectedRecords.map((record: CheckinRecord) =>
        formatRecordForCSV(record)
      ),
    ];

    // Create and download CSV file
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `employee_checkin_selected_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clear selection after export
    setSelectedRows(new Set());
  };

  // Export all data to CSV
  const handleExport = async () => {
    try {
      // Build filter parameters (same as the query)
      const paramsFilter: any = {};

      // Add date filters
      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.datetime__gte = formattedStartDate;
        }
      }
      if (endDate && endDate.trim() !== "") {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.datetime__lte = formattedEndDate;
        }
      }

      // Add employee ID filter if a specific employee is selected (not "all")
      if (selectedEmployee && selectedEmployee !== "all") {
        paramsFilter.employee__eq = selectedEmployee;
      }

      // Add logType filter if a specific logType is selected (not "all")
      if (selectedLogType && selectedLogType !== "all" && logTypeField) {
        paramsFilter[`${logTypeField}__eq`] = selectedLogType;
      }

      paramsFilter.ordering = '["-datetime", "employee.firstname"]';

      // Fetch all data with a large page size
      const allDataResponse = await DynamicModuleService.getItemsByPage(
        EMPLOYEE_CHECKIN_SCHEMA_NAME,
        1,
        10000, // Large page size to get all records
        paramsFilter
      );

      // Extract all records from response
      let allRecords: any[] = [];
      if (Array.isArray(allDataResponse)) {
        allRecords = allDataResponse;
      } else if (
        allDataResponse.results &&
        Array.isArray(allDataResponse.results)
      ) {
        allRecords = allDataResponse.results;
      }

      // Map records to CheckinRecord format using the same mapping function
      const mappedAllRecords: CheckinRecord[] = allRecords.map((record: any) =>
        mapRecordToCheckinRecord(record)
      );

      // Prepare CSV data
      const headers = ["Employee Name", "Date", "Time", "Log Type"];

      const csvRows = [
        headers.join(","),
        ...mappedAllRecords.map((record: CheckinRecord) =>
          formatRecordForCSV(record)
        ),
      ];

      // Create and download CSV file
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      // Generate filename with date range if applicable
      let filename = `employee_checkin_report_${new Date().toISOString().split("T")[0]}`;
      if (startDate && endDate) {
        filename = `employee_checkin_report_${startDate}_to_${endDate}`;
      } else if (startDate) {
        filename = `employee_checkin_report_from_${startDate}`;
      } else if (endDate) {
        filename = `employee_checkin_report_until_${endDate}`;
      }

      link.setAttribute("download", `${filename}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error("Export error:", error);
      notify.error(error?.message ?? "Failed to export check-in records");
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Employee Check-In
          </h1>
          <p className="text-sm text-[#627384]">
            Employee check-in/check-out tracking and reporting
          </p>
        </div>
        <div className="flex items-center gap-2">
          {userCanManageCheckin && (
            <Button
              onClick={handleAdd}
              className="border-primary hover:bg-primary text-primary flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:text-white"
            >
              <Plus className="h-4 w-4" />
              Add Check-In
            </Button>
          )}
          <Button
            onClick={handleExport}
            className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
          >
            <Download className="h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="startDate" className="text-sm text-gray-700">
                Start Date
              </Label>
              <div className="relative">
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value || "")}
                  className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                />
                {startDate && startDate !== defaultDates.startDate && (
                  <button
                    onClick={() => {
                      const defaults = getDefaultDates();
                      setStartDate(defaults.startDate);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Reset to default"
                    type="button"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-sm text-gray-700">
                End Date
              </Label>
              <div className="relative">
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value || "")}
                  className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                />
                {endDate && endDate !== defaultDates.endDate && (
                  <button
                    onClick={() => {
                      const today = new Date();
                      setEndDate(today.toISOString().split("T")[0]);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Reset to today"
                    type="button"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            {/* Employee filter - Only show for admin/user roles, hide for employee role */}
            {!userIsEmployee && (
              <div className="space-y-2">
                <Label htmlFor="employee" className="text-sm text-gray-700">
                  Employee
                </Label>
                <Select
                  id="employee"
                  allowClear
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  placeholder="Select employee"
                  className="h-[2rem] w-full rounded-xl"
                  showSearch
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "");
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  options={[
                    { value: "all", label: "All Employees" },
                    ...(employeeOptionsWithNames?.map(
                      (employee: { value: string; label: string }) => ({
                        value: employee.value,
                        label: employee.label,
                      })
                    ) ?? []),
                  ]}
                />
              </div>
            )}
            {/* LogType filter */}
            <div className="space-y-2">
              <Label htmlFor="logType" className="text-sm text-gray-700">
                Log Type
              </Label>
              <Select
                id="logType"
                allowClear
                value={selectedLogType}
                onChange={setSelectedLogType}
                placeholder="Select log type"
                className="h-[2rem] w-full rounded-xl"
                options={[
                  { value: "all", label: "All Log Types" },
                  ...logTypeOptions.map((logType: string) => ({
                    value: logType,
                    label: logType,
                  })),
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Check-In History Table */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-0">
          <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between md:gap-4">
            <h2 className="text-primary text-lg font-bold">
              Employee Check-In History
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {mappedRecords.length} Records
              </span>
              {selectedRows.size > 0 && exportableSelectedCount > 0 && (
                <>
                  <Button
                    onClick={handleExportSelected}
                    variant="outline"
                    size="sm"
                    className="text-primary hover:bg-secondary flex items-center gap-2 border hover:text-white"
                  >
                    <Download className="h-4 w-4" />
                    Export ({exportableSelectedCount})
                  </Button>
                  {userCanManageCheckin && (
                    <Popconfirm
                      title={`Are you sure you want to delete ${selectedRows.size} selected check-in record(s)?`}
                      description="This action cannot be undone."
                      onConfirm={handleBulkDeleteCheckin}
                      okText="Yes, Delete"
                      cancelText="Cancel"
                      okButtonProps={{
                        danger: true,
                        loading: bulkDeleteCheckinMutation.isPending,
                      }}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 border-red-300 text-red-600 hover:border-red-400 hover:bg-red-50 hover:text-red-700"
                        disabled={bulkDeleteCheckinMutation.isPending}
                      >
                        <Trash className="h-4 w-4" />
                        Delete ({selectedRows.size})
                      </Button>
                    </Popconfirm>
                  )}
                </>
              )}
              {/* <Button
                onClick={handleExport}
                variant="outline"
                size="sm"
                className="text-primary hover:bg-secondary flex items-center gap-2 border hover:text-white"
              >
                <Download className="h-4 w-4" />
                Export All
              </Button> */}
            </div>
          </div>

          {/* Responsive Table Container */}
          <div className="w-full overflow-x-auto">
            {isLoading ? (
              <div className="flex h-64 items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-12 text-center">
                      <input
                        type="checkbox"
                        checked={allRowsSelected}
                        onChange={e => handleSelectAll(e.target.checked)}
                        // className="text-primary focus:ring-primary h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2"
                        className="[&:checked]:bg-primary [&:checked]:border-primary focus:ring-primary accent-primary h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2 [&:checked]:text-white"
                        style={{
                          accentColor: "#0c3055",
                        }}
                        title="Select all"
                      />
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">
                      Employee Name
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">
                      Date
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">
                      Time
                    </TableHead>
                    <TableHead className="text-center font-semibold text-gray-700">
                      Log Type
                    </TableHead>
                    {userCanManageCheckin && (
                      <TableHead className="pe-8 text-right font-semibold text-gray-700">
                        Action
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={userCanManageCheckin ? 6 : 5}
                        className="py-8 text-center text-gray-500"
                      >
                        No check-in records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    mappedRecords.map(
                      (record: CheckinRecord & { rawRecord?: any }) => {
                        const isSelected = selectedRows.has(record.id);

                        // Check if check-in record was edited: created_by != updated_by || created_on != updated_on
                        const rawRecord = record.rawRecord;
                        const createdBy = rawRecord?.created_by;
                        const updatedBy = rawRecord?.updated_by;
                        const createdOn = rawRecord?.created_on;
                        const updatedOn = rawRecord?.updated_on;
                        const isEdited =
                          createdBy !== updatedBy || createdOn !== updatedOn;
                        const formattedDate = isEdited
                          ? formatDateTime(updatedOn)
                          : "";

                        return (
                          <TableRow
                            key={record.id}
                            className="hover:bg-gray-50"
                          >
                            <TableCell className="w-12 text-center align-middle">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={e =>
                                  handleRowSelect(record.id, e.target.checked)
                                }
                                // className="text-primary focus:ring-primary h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2"
                                className="[&:checked]:bg-primary [&:checked]:border-primary focus:ring-primary accent-primary h-4 w-4 cursor-pointer rounded border-gray-300 focus:ring-2 [&:checked]:text-white"
                                style={{
                                  accentColor: "#0c3055",
                                }}
                                onClick={e => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="ps-5 align-middle font-medium text-gray-900">
                              <div className="flex flex-col gap-1">
                                <span>
                                  {record.firstName} {record.lastName}
                                </span>
                                {isEdited && (
                                  <Tooltip
                                    title={
                                      <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                          <User
                                            size={14}
                                            className="text-gray-800"
                                          />
                                          <EmployeeTooltipContent
                                            userId={updatedBy}
                                          />
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Clock
                                            size={14}
                                            className="text-gray-800"
                                          />
                                          <span className="text-gray-700">
                                            {formattedDate}
                                          </span>
                                        </div>
                                      </div>
                                    }
                                    placement="bottom"
                                    overlayInnerStyle={{
                                      backgroundColor: "#ffffff",
                                      color: "#374151",
                                      borderRadius: "8px",
                                      border: "1px solid #e0e6eb",
                                      padding: "8px 12px",
                                      boxShadow:
                                        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                    }}
                                  >
                                    <div className="flex cursor-pointer items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                                      <Pencil size={12} />
                                      <span>Edited</span>
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="align-middle text-gray-700">
                              {formatDate(record.date || record.time)}
                            </TableCell>
                            <TableCell className="align-middle text-gray-700">
                              {formatTime(record.time)}
                            </TableCell>
                            <TableCell className="text-center align-middle">
                              <Badge
                                className={`${getLogTypeBadgeColor(record.logType)} border-0 text-xs`}
                              >
                                {record.logType}
                              </Badge>
                            </TableCell>
                            {userCanManageCheckin && (
                              <TableCell className="pe-5 align-middle">
                                <div className="flex items-center justify-end gap-2">
                                  <CustomButton
                                    icon={<Edit2 size={14} />}
                                    type="text"
                                    onClick={() => handleEdit(record.id)}
                                    title="Edit"
                                  />
                                  <Popconfirm
                                    title="Are you sure you want to delete this record?"
                                    onConfirm={() => handleDelete(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <CustomButton
                                      danger
                                      icon={<Trash size={14} />}
                                      type="text"
                                      title="Delete"
                                    />
                                  </Popconfirm>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }
                    )
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          {/* Pagination - Always show if there are records or loading */}
          <div className="flex items-center justify-end border-t p-4">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              onChange={(page, size) => {
                // This will trigger a new API call because currentPage/pageSize are in queryKey
                if (size && size !== pageSize) {
                  // Reset to page 1 when page size changes
                  setCurrentPage(1);
                  setPageSize(size);
                  setSelectedRows(new Set()); // Clear selected rows when page size changes
                } else {
                  setCurrentPage(page);
                  setSelectedRows(new Set()); // Clear selected rows when page changes
                }
              }}
              onShowSizeChange={(_current, size) => {
                // Handle page size change separately
                setCurrentPage(1);
                setPageSize(size);
                setSelectedRows(new Set()); // Clear selected rows when page size changes
              }}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} items`
              }
              pageSizeOptions={["50", "100", "200", "300"]}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Edit/Add Modal */}
      <Dialog
        open={modalOpen}
        onOpenChange={open => {
          if (!open && !isSubmitting) {
            handleModalCancel();
          }
        }}
      >
        <DialogContent className="bg-white sm:max-w-[500px]">
          <style>{`
            /* Ensure Ant Design dropdowns appear above dialog */
            .ant-select-dropdown,
            .ant-picker-dropdown,
            .ant-cascader-dropdown,
            .ant-tree-select-dropdown,
            .ant-select-dropdown:not(.ant-select-dropdown-hidden),
            .ant-picker-dropdown:not(.ant-picker-dropdown-hidden),
            .ant-cascader-dropdown:not(.ant-cascader-dropdown-hidden),
            .ant-tree-select-dropdown:not(.ant-tree-select-dropdown-hidden) {
              z-index: 1050 !important;
            }
            /* Ensure dropdowns are clickable */
            .ant-select-dropdown,
            .ant-picker-dropdown,
            .ant-cascader-dropdown,
            .ant-tree-select-dropdown {
              pointer-events: auto !important;
            }
          `}</style>
          <DialogHeader>
            <DialogTitle>
              {editingRecord?.id
                ? "Edit Check-In Record"
                : "Add Check-In Record"}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {schemaInfo ? (
              <ConfigProvider
                getPopupContainer={triggerNode =>
                  triggerNode?.parentElement ?? document.body
                }
                dropdownMatchSelectWidth={false}
              >
                <SchemaFormView
                  key={
                    editingRecord?.id ? `edit-${editingRecord.id}` : "add-new"
                  }
                  form={form}
                  schemaJson={getModifiedSchema()}
                />
              </ConfigProvider>
            ) : (
              <div className="flex h-64 items-center justify-center">
                <LoadingSpinner />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleModalCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleModalSave}
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {isSubmitting
                ? "Saving..."
                : editingRecord?.id
                  ? "Update"
                  : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeCheckinPage;
